<script lang="ts">
  import { onMount, untrack } from 'svelte';
  import type { DateValue } from '@internationalized/date';
  import { Button } from '$lib/components/ui/button';
  import DatePicker from '$lib/components/DatePicker.svelte';
  import DateRangePicker from '$lib/components/DateRangePicker.svelte';
  import GuestsInput from '$lib/components/GuestsInput.svelte';
  import Skeleton from '$lib/components/Skeleton.svelte';
  import { buildRedirectUrl, fetchBestPrice, formatPrice } from '$lib/api';
  import {
    addNights,
    nightsBetween,
    todayDate,
    toISODate,
    tomorrow,
  } from '$lib/dates';
  import {
    isFutureOrToday,
    loadSelection,
    parseISODate,
    saveRedirectUrl,
    saveSelection,
  } from '$lib/storage';
  import type { PricingState, WidgetConfig } from '$lib/types';
  import { t } from '$lib/i18n';

  type Props = {
    config: WidgetConfig;
    portalTarget?: HTMLElement | null;
  };

  let { config, portalTarget = null }: Props = $props();

  const i18n = $derived(t(config.lang));

  function getInitialSelection(): {
    checkin: DateValue;
    checkout: DateValue;
    guests: number;
  } {
    const fallback = {
      checkin: tomorrow() as DateValue,
      checkout: addNights(tomorrow(), 1) as DateValue,
      guests: 2,
    };
    const saved = loadSelection();
    if (!saved) return fallback;
    const cIn = parseISODate(saved.checkin);
    const cOut = parseISODate(saved.checkout);
    if (!cIn || !cOut) return fallback;
    if (!isFutureOrToday(cIn)) return fallback;
    if (nightsBetween(cIn, cOut) < 1) return fallback;
    return { checkin: cIn, checkout: cOut, guests: saved.guests };
  }

  const initial = getInitialSelection();
  let checkin = $state<DateValue>(initial.checkin);
  let checkout = $state<DateValue>(initial.checkout);
  let guests = $state<number>(initial.guests);

  let pricing = $state<PricingState>({ status: 'idle' });
  let currentController: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  const nights = $derived(nightsBetween(checkin, checkout));
  const query = $derived({
    startdate: toISODate(checkin),
    enddate: toISODate(checkout),
    residents: guests,
  });

  const bothUnavailable = $derived(
    pricing.status === 'success' &&
      pricing.data.bestPrice < 0 &&
      pricing.data.competitorPrice < 0,
  );

  const showBookButton = $derived(config.showBookButton !== false);
  const showPricing = $derived(config.showPricing !== false);

  const showInlinePricing = $derived(
    showPricing &&
      (pricing.status === 'loading' ||
        (pricing.status === 'success' && !bothUnavailable)),
  );

  const discountPercent = $derived.by(() => {
    if (pricing.status !== 'success') return null;
    const { bestPrice, competitorPrice } = pricing.data;
    if (bestPrice <= 0 || competitorPrice <= 0) return null;
    if (bestPrice >= competitorPrice) return null;
    return Math.round(((competitorPrice - bestPrice) / competitorPrice) * 100);
  });

  function setRange(r: { start: DateValue; end: DateValue }) {
    checkin = r.start;
    checkout =
      nightsBetween(r.start, r.end) >= 1 ? r.end : addNights(r.start, 1);
  }

  function setCheckin(v: DateValue) {
    const gap = Math.max(1, nightsBetween(checkin, checkout));
    checkin = v;
    checkout = addNights(v, gap);
  }

  function setCheckout(v: DateValue) {
    if (nightsBetween(checkin, v) >= 1) {
      checkout = v;
    }
  }

  function setGuests(v: number) {
    guests = v;
  }

  async function loadPrice() {
    if (!config.token) {
      pricing = {
        status: 'error',
        message: i18n.missingToken,
      };
      return;
    }
    currentController?.abort();
    const ctrl = new AbortController();
    currentController = ctrl;
    pricing = { status: 'loading' };
    try {
      const data = await fetchBestPrice(config, untrack(() => query), ctrl.signal);
      if (ctrl.signal.aborted) return;
      pricing = { status: 'success', data };
    } catch (err) {
      if (ctrl.signal.aborted) return;
      const message = err instanceof Error ? err.message : i18n.unknownError;
      pricing = { status: 'error', message };
    }
  }

  function scheduleLoad() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(loadPrice, 300);
  }

  onMount(() => {
    loadPrice();
    return () => {
      currentController?.abort();
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  });

  let hasMounted = false;

  $effect(() => {
    query.startdate;
    query.enddate;
    query.residents;
    if (!hasMounted) {
      hasMounted = true;
      return;
    }
    untrack(() => scheduleLoad());
  });

  $effect(() => {
    saveSelection({
      checkin: query.startdate,
      checkout: query.enddate,
      guests: query.residents,
    });
  });

  $effect(() => {
    saveRedirectUrl(buildRedirectUrl(config, query));
  });

  function handleBook() {
    const url = buildRedirectUrl(config, query);
    window.open(url, '_blank', 'noopener,noreferrer');
  }


</script>

<div
  class="qs-root quick-search-root @container w-full rounded-xl border border-border bg-card p-4 text-card-foreground shadow-sm @sm:p-6"
>
  <div
    class="qs-grid grid grid-cols-1 items-stretch gap-2 @sm:grid-cols-2 @2xl:gap-3"
  >
    {#if config.separateDates}
      <DatePicker
        label={i18n.checkin}
        value={checkin}
        onValueChange={setCheckin}
        minValue={todayDate()}
        locale={config.lang}
        {portalTarget}
      />
      <DatePicker
        label={i18n.checkout}
        value={checkout}
        onValueChange={setCheckout}
        minValue={addNights(checkin, 1)}
        locale={config.lang}
        {portalTarget}
      />
    {:else}
      <DateRangePicker
        checkinLabel={i18n.checkin}
        checkoutLabel={i18n.checkout}
        {checkin}
        {checkout}
        onValueChange={setRange}
        minValue={todayDate()}
        locale={config.lang}
        {portalTarget}
      />
    {/if}
    <GuestsInput
      label={i18n.guests}
      value={guests}
      onValueChange={setGuests}
      decreaseLabel={i18n.decrease}
      increaseLabel={i18n.increase}
    />
    {#if showBookButton}
      <Button
        class="qs-book-btn min-h-[42px] self-stretch px-4 text-sm font-semibold @2xl:text-base"
        disabled={bothUnavailable}
        onclick={handleBook}
      >
        {i18n.book}
      </Button>
    {/if}

    {#if showInlinePricing}
      <div
        aria-live="polite"
        aria-busy={pricing.status === 'loading'}
        class="qs-pricing flex flex-col items-start justify-center self-stretch border-t border-border pt-3 @sm:col-span-2 @2xl:border-t-0 @2xl:pl-2 @2xl:pt-0"
      >
        {#if pricing.status === 'loading'}
          <div
            class="qs-pricing-loading grid grid-cols-[auto_auto] items-baseline gap-x-2 gap-y-1"
          >
            <Skeleton class="h-3 w-16" />
            <Skeleton class="h-5 w-24" />
            <Skeleton class="h-3 w-16" />
            <Skeleton class="h-3 w-16" />
          </div>
        {:else if pricing.status === 'success'}
          {@const { bestPrice, competitorPrice } = pricing.data}
          <div
            class="qs-pricing-details grid grid-cols-[auto_auto] items-baseline gap-x-2 gap-y-0.5 whitespace-nowrap"
          >
            <span
              class="qs-label-official text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
            >
              {i18n.officialSite}
            </span>
            {#if bestPrice > 0}
              <span class="qs-best-price flex items-center gap-1.5">
                <span class="qs-best-price-value text-lg font-bold leading-tight text-foreground">
                  {formatPrice(bestPrice, config.lang)}
                </span>
                {#if discountPercent !== null}
                  <span
                    class="qs-discount-badge rounded-full bg-success px-1.5 py-0.5 text-[10px] font-semibold leading-none text-success-foreground"
                  >
                    -{discountPercent}%
                  </span>
                {/if}
              </span>
            {:else}
              <span class="qs-best-price-unavailable text-sm italic text-muted-foreground">
                {i18n.unavailable}
              </span>
            {/if}

            {#if competitorPrice !== bestPrice}
              <span
                class="qs-label-competitor text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
              >
                {i18n.otherSites}
              </span>
              {#if competitorPrice > 0}
                <span
                  class="qs-competitor-price text-xs leading-tight text-muted-foreground line-through"
                >
                  {formatPrice(competitorPrice, config.lang)}
                </span>
              {:else}
                <span class="qs-competitor-price-unavailable text-xs italic text-muted-foreground">
                  {i18n.unavailable}
                </span>
              {/if}
            {/if}
          </div>
        {/if}
      </div>
    {/if}
  </div>

  <div
    class="qs-footer mt-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-xs text-muted-foreground"
  >
    <span class="qs-nights-summary">
      {i18n.nightsSummary(nights, guests)}
    </span>
    {#if pricing.status === 'error'}
      <span class="qs-error text-right text-destructive" aria-live="polite">
        {pricing.message}
      </span>
    {:else if pricing.status === 'success' && bothUnavailable && showPricing}
      <span class="qs-no-rate text-right" aria-live="polite">
        {i18n.noRateAvailable}
      </span>
    {/if}
  </div>
</div>
